import { useState, useEffect, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import {
  FiPlus, FiSearch, FiEdit2, FiUpload, FiDownload, FiX, FiUserCheck, FiUserX, FiTrash2,
  FiEye, FiCalendar, FiFileText, FiClipboard, FiDollarSign, FiUser
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function Students() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailTab, setDetailTab] = useState('attendance');
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState({
    attendance: [],
    lessons: [],
    tests: [],
    tuitions: [],
    counsels: []
  });
  const [editingStudent, setEditingStudent] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [missingClasses, setMissingClasses] = useState([]);  // 없는 반 목록
  const [showMissingClassModal, setShowMissingClassModal] = useState(false);
  const [missingClassForms, setMissingClassForms] = useState({});  // 반 생성 폼 데이터
  const [gradeWarnings, setGradeWarnings] = useState([]);  // 학년 불일치 경고
  const [pendingStudents, setPendingStudents] = useState([]);  // 업로드 대기 중인 학생들
  const [showGradeWarningModal, setShowGradeWarningModal] = useState(false);  // 학교/학년 불일치 수정 모달
  const [gradeWarningStudents, setGradeWarningStudents] = useState([]);  // 수정 가능한 불일치 학생 목록
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '123456',
    role: 'student',
    studentInfo: {
      school: '',
      grade: '',
      parentName: '',
      parentPhone: ''
    },
    classId: ''  // 반 선택
  });
  const [showNewClassInput, setShowNewClassInput] = useState(false);
  const [newClassData, setNewClassData] = useState({
    name: '',
    subject: '',
    grade: '',
    maxStudents: 20,
    tuitionFee: 0,
    scheduleText: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // 강사인 경우 맡은 학생만 필터링
  const filteredStudents = useMemo(() => {
    if (isAdmin) return students;

    // 강사가 맡은 반의 학생 ID 목록 추출
    const myStudentIds = new Set();
    classes.forEach(cls => {
      const isMyClass = cls.teachers?.some(t => (t._id || t) === user?._id);
      if (isMyClass && cls.students) {
        cls.students.forEach(s => myStudentIds.add(s._id || s));
      }
    });

    return students.filter(s => myStudentIds.has(s._id));
  }, [students, classes, isAdmin, user?._id]);

  const loadData = async () => {
    try {
      const [studentsRes, classesRes] = await Promise.all([
        api.get('/api/users', { params: { role: 'student' } }),
        api.get('/api/class-groups')
      ]);
      setStudents(studentsRes.data.users || []);
      setClasses(classesRes.data.classGroups || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/users', { params: { role: 'student', search } });
      setStudents(data.users || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (student = null) => {
    if (student) {
      setEditingStudent(student);
      // 학생이 속한 반 찾기
      const studentClass = classes.find(c =>
        c.students?.some(s => (s._id || s) === student._id)
      );
      setFormData({
        name: student.name,
        email: student.email,
        phone: student.phone || '',
        password: '',
        role: 'student',
        studentInfo: {
          school: student.studentInfo?.school || '',
          grade: student.studentInfo?.grade || '',
          parentName: student.studentInfo?.parentName || '',
          parentPhone: student.studentInfo?.parentPhone || ''
        },
        classId: studentClass?._id || ''
      });
    } else {
      setEditingStudent(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '123456',
        role: 'student',
        studentInfo: {
          school: '',
          grade: '',
          parentName: '',
          parentPhone: ''
        },
        classId: ''
      });
    }
    setShowNewClassInput(false);
    setNewClassData({
      name: '',
      subject: '',
      grade: '',
      maxStudents: 20,
      tuitionFee: 0,
      scheduleText: ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 학교/학년 불일치 검증
    const { school, grade } = formData.studentInfo;
    if (school && grade && !isSchoolGradeMatch(school, grade)) {
      const schoolType = school.includes('초등학교') ? '초등학교' : school.includes('중학교') ? '중학교' : school.includes('고등학교') ? '고등학교' : '';
      const expectedGrades = schoolType === '초등학교' ? '초1~초6' : schoolType === '중학교' ? '중1~중3' : schoolType === '고등학교' ? '고1~고3' : '';
      alert(`학교와 학년이 일치하지 않습니다.\n\n학교: ${school}\n입력된 학년: ${grade}\n예상 학년: ${expectedGrades}`);
      return;
    }

    // 기존 반에 배정 시 학년 불일치 검증
    if (formData.classId && !showNewClassInput) {
      const targetClass = classes.find(c => c._id === formData.classId);
      if (targetClass && targetClass.grade && grade) {
        if (!isGradeMatch(grade, targetClass.grade)) {
          const proceed = window.confirm(
            `학생 학년과 반의 대상 학년이 일치하지 않습니다.\n\n` +
            `학생 학년: ${grade}\n` +
            `반 대상 학년: ${targetClass.grade}\n\n` +
            `그래도 이 반에 배정하시겠습니까?`
          );
          if (!proceed) return;
        }
      }
    }

    try {
      let classIdToAssign = formData.classId;

      // 새 반 생성
      if (showNewClassInput && newClassData.name.trim()) {
        try {
          const schedule = parseScheduleText(newClassData.scheduleText);
          const { data: createdClassData } = await api.post('/api/class-groups', {
            name: newClassData.name.trim(),
            subject: newClassData.subject,
            grade: newClassData.grade,
            maxStudents: parseInt(newClassData.maxStudents) || 20,
            tuitionFee: parseInt(newClassData.tuitionFee) || 0,
            schedule
          });
          classIdToAssign = createdClassData.classGroup._id;
          // 반 목록 새로고침
          const { data: classesData } = await api.get('/api/class-groups');
          setClasses(classesData.classGroups || []);
        } catch (err) {
          alert(`반 생성 실패: ${err.response?.data?.error || err.message}`);
          return;
        }
      }

      // 학생 데이터 준비 (classId 제외)
      const studentData = { ...formData };
      delete studentData.classId;

      let studentId;

      if (editingStudent) {
        await api.put(`/api/users/${editingStudent._id}`, studentData);
        studentId = editingStudent._id;

        // 기존 반에서 제거 (다른 반에 있었다면)
        const oldClass = classes.find(c =>
          c.students?.some(s => (s._id || s) === studentId)
        );
        if (oldClass && oldClass._id !== classIdToAssign) {
          await api.delete(`/api/class-groups/${oldClass._id}/students/${studentId}`);
        }
      } else {
        const { data: userData } = await api.post('/api/users', studentData);
        studentId = userData.user.id;
      }

      // 선택된 반에 학생 배정
      if (classIdToAssign && studentId) {
        const targetClass = classes.find(c => c._id === classIdToAssign);
        const alreadyInClass = targetClass?.students?.some(s => (s._id || s) === studentId);
        if (!alreadyInClass) {
          await api.post(`/api/class-groups/${classIdToAssign}/students`, {
            studentIds: [studentId]
          });
        }
      }

      setShowModal(false);
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.error || '';
      if (errorMsg.includes('duplicate') || errorMsg.includes('already exists') || errorMsg.includes('E11000')) {
        alert(`이메일이 이미 사용 중입니다.\n\n다른 이메일로 변경해주세요.\n\n입력된 이메일: ${formData.email}`);
      } else {
        alert(errorMsg || '저장에 실패했습니다');
      }
    }
  };

  const handleToggleActive = async (student) => {
    const action = student.isActive ? '비활성화' : '활성화';
    if (!window.confirm(`${student.name} 학생을 ${action}하시겠습니까?`)) return;

    try {
      await api.put(`/api/users/${student._id}`, { isActive: !student.isActive });
      loadData();
    } catch (error) {
      alert(`${action}에 실패했습니다`);
    }
  };

  const handleDelete = async (student) => {
    if (!window.confirm(`${student.name} 학생을 정말 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;

    try {
      await api.delete(`/api/users/${student._id}?permanent=true`);
      loadData();
    } catch (error) {
      alert('삭제에 실패했습니다');
    }
  };

  // Student Detail Modal
  const openDetailModal = async (student) => {
    setSelectedStudent(student);
    setDetailTab('attendance');
    setShowDetailModal(true);
    setDetailLoading(true);

    try {
      const [attendanceRes, lessonsRes, testsRes, tuitionsRes, counselsRes] = await Promise.all([
        api.get('/api/attendance', { params: { studentId: student._id, limit: 50 } }),
        api.get('/api/lessons', { params: { studentId: student._id, limit: 30 } }),
        api.get('/api/tests/student/' + student._id),
        api.get('/api/tuition/student/' + student._id),
        api.get('/api/counsel/student/' + student._id)
      ]);

      setStudentDetail({
        attendance: attendanceRes.data.records || [],
        lessons: lessonsRes.data.lessons || [],
        tests: testsRes.data.results || [],
        tuitions: tuitionsRes.data.tuitions || [],
        counsels: counselsRes.data.counsels || []
      });
    } catch (error) {
      console.error('Failed to load student detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const getAttendanceStatus = (status) => {
    const labels = { present: '출석', absent: '결석', late: '지각', excused: '인정결석' };
    const colors = { present: 'success', absent: 'danger', late: 'warning', excused: 'info' };
    return { label: labels[status] || status, color: colors[status] || 'default' };
  };

  const getTuitionStatus = (status) => {
    const labels = { unpaid: '미납', partial: '부분납', paid: '완납', cancelled: '취소' };
    const colors = { unpaid: 'danger', partial: 'warning', paid: 'success', cancelled: 'default' };
    return { label: labels[status] || status, color: colors[status] || 'default' };
  };

  // 전화번호 포맷팅 (01012345678 → 010-1234-5678)
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // 숫자만 추출
    const numbers = String(phone).replace(/[^0-9]/g, '');

    // 11자리 (010-xxxx-xxxx)
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    // 10자리 (02-xxxx-xxxx 또는 010-xxx-xxxx)
    if (numbers.length === 10) {
      if (numbers.startsWith('02')) {
        return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
      }
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    // 이미 포맷이 있거나 다른 경우 그대로 반환
    return String(phone);
  };

  // 학년이 반의 대상 학년에 포함되는지 확인
  // 예: "중1-2" → 중1, 중2 포함 / "중1-3" → 중1, 중2, 중3 포함
  const isGradeMatch = (studentGrade, classGrade) => {
    if (!studentGrade || !classGrade) return true;  // 정보 없으면 경고 안함

    // 정확히 일치하면 OK
    if (classGrade === studentGrade) return true;

    // 반 학년이 학생 학년을 직접 포함하면 OK (예: "중2" in "중1, 중2")
    if (classGrade.includes(studentGrade)) return true;

    // 범위 형식 처리 (예: "중1-2", "중1-3", "초5-6", "고1-3")
    const rangeMatch = classGrade.match(/^(초|중|고)(\d)-(\d)$/);
    if (rangeMatch) {
      const [, prefix, startNum, endNum] = rangeMatch;
      const start = parseInt(startNum);
      const end = parseInt(endNum);

      // 학생 학년에서 prefix와 숫자 추출 (예: "중2" → "중", 2)
      const studentMatch = studentGrade.match(/^(초|중|고)(\d)$/);
      if (studentMatch) {
        const [, studentPrefix, studentNum] = studentMatch;
        const num = parseInt(studentNum);

        // prefix가 같고 숫자가 범위 안에 있으면 OK
        if (prefix === studentPrefix && num >= start && num <= end) {
          return true;
        }
      }
    }

    return false;
  };

  // 학교명 정규화 (초→초등학교, 중→중학교, 고→고등학교)
  const normalizeSchoolName = (school) => {
    if (!school) return '';
    let normalized = String(school).trim();
    // 이미 완전한 형태면 그대로 반환
    if (normalized.endsWith('초등학교') || normalized.endsWith('중학교') || normalized.endsWith('고등학교')) {
      return normalized;
    }
    // 축약형 변환
    if (normalized.endsWith('초')) {
      return normalized.slice(0, -1) + '초등학교';
    }
    if (normalized.endsWith('중')) {
      return normalized.slice(0, -1) + '중학교';
    }
    if (normalized.endsWith('고')) {
      return normalized.slice(0, -1) + '고등학교';
    }
    return normalized;
  };

  // 학교와 학년이 일치하는지 확인
  const isSchoolGradeMatch = (school, grade) => {
    if (!school || !grade) return true;  // 정보 없으면 OK
    const normalizedSchool = normalizeSchoolName(school);
    if (normalizedSchool.includes('초등학교')) {
      return grade.startsWith('초');
    }
    if (normalizedSchool.includes('중학교')) {
      return grade.startsWith('중');
    }
    if (normalizedSchool.includes('고등학교')) {
      return grade.startsWith('고');
    }
    return true;
  };

  // 수업시간 텍스트 파싱 (예: "월 14:00-16:00, 수 14:00-16:00")
  const parseScheduleText = (scheduleStr) => {
    if (!scheduleStr) return [];
    const schedules = [];
    const parts = String(scheduleStr).split(/[,，]\s*/).map(s => s.trim());

    parts.forEach(part => {
      const normalized = part
        .replace(/\s+/g, ' ')
        .replace(/：/g, ':')
        .replace(/[–—～~]/g, '-')
        .replace(/\s*:\s*/g, ':')
        .replace(/\s*-\s*/g, '-')
        .trim();

      const match = normalized.match(/([월화수목금토일])\s*(\d{1,2}:\d{2})-(\d{1,2}:\d{2})/);
      if (match) {
        schedules.push({
          day: match[1],
          startTime: match[2].padStart(5, '0'),
          endTime: match[3].padStart(5, '0')
        });
      }
    });

    return schedules;
  };

  // Excel Upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadFile(file);

    // Parse xlsx/xls file preview
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          setUploadPreview([]);
          return;
        }

        const headers = jsonData[0].map(h => String(h).trim());
        const preview = [];
        for (let i = 1; i < Math.min(jsonData.length, 6); i++) {
          const row = {};
          headers.forEach((h, idx) => {
            row[h] = jsonData[i][idx] || '';
          });
          preview.push(row);
        }
        setUploadPreview(preview);
      } catch (err) {
        console.error('Failed to parse file:', err);
        alert('파일을 읽는데 실패했습니다');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // 엑셀 파일 분석 및 검증
  const handleUpload = async () => {
    if (!uploadFile) return;

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const studentsToUpload = [];
          const classNamesInFile = new Set();
          const warnings = [];
          let idx = 0;

          for (const row of jsonData) {
            idx++;
            const name = row['이름'] || row['name'] || row['Name'];
            if (!name) continue;

            const className = row['반'] || row['class'] || '';
            const studentGrade = row['학년'] || row['grade'] || '';

            if (className) {
              classNamesInFile.add(className);
            }

            // 전화번호 포맷팅 적용 (앞자리 0이 빠진 경우 복원)
            let rawPhone = row['전화번호'] || row['phone'] || '';
            let rawParentPhone = row['학부모연락처'] || row['parentPhone'] || '';

            // 숫자로 읽힌 경우 앞에 0 추가 (10자리면 앞에 0 추가)
            if (typeof rawPhone === 'number') rawPhone = String(rawPhone);
            if (typeof rawParentPhone === 'number') rawParentPhone = String(rawParentPhone);
            if (rawPhone && !rawPhone.startsWith('0') && rawPhone.replace(/[^0-9]/g, '').length === 10) {
              rawPhone = '0' + rawPhone;
            }
            if (rawParentPhone && !rawParentPhone.startsWith('0') && rawParentPhone.replace(/[^0-9]/g, '').length === 10) {
              rawParentPhone = '0' + rawParentPhone;
            }

            const phone = formatPhoneNumber(rawPhone);
            const parentPhone = formatPhoneNumber(rawParentPhone);

            // 학교명 정규화 (초→초등학교, 중→중학교, 고→고등학교)
            const school = normalizeSchoolName(row['학교'] || row['school'] || '');

            studentsToUpload.push({
              name,
              email: row['이메일'] || row['email'] || `student${Date.now()}${idx}@temp.com`,
              phone,
              password: row['비밀번호'] || row['password'] || '123456',
              role: 'student',
              studentInfo: {
                school,
                grade: studentGrade,
                parentName: row['학부모명'] || row['parentName'] || '',
                parentPhone
              },
              className
            });
          }

          // 없는 반 확인
          const missingClassNames = [];
          classNamesInFile.forEach(className => {
            const exists = classes.find(c => c.name === className);
            if (!exists) {
              missingClassNames.push(className);
            }
          });

          // 학년 불일치 확인 (반의 대상 학년과 학생 학년)
          studentsToUpload.forEach(student => {
            if (student.className) {
              const classGroup = classes.find(c => c.name === student.className);
              if (classGroup && !isGradeMatch(student.studentInfo.grade, classGroup.grade)) {
                warnings.push({
                  type: 'classGrade',
                  student: student.name,
                  studentGrade: student.studentInfo.grade,
                  className: student.className,
                  classGrade: classGroup.grade
                });
              }
            }
          });

          // 학교/학년 불일치 확인 (학교명과 학년이 맞지 않는 경우)
          const schoolGradeMismatches = [];
          studentsToUpload.forEach((student, index) => {
            if (!isSchoolGradeMatch(student.studentInfo.school, student.studentInfo.grade)) {
              schoolGradeMismatches.push({
                index,
                name: student.name,
                school: student.studentInfo.school,
                grade: student.studentInfo.grade,
                newGrade: student.studentInfo.grade  // 수정할 학년 (초기값은 기존 값)
              });
            }
          });

          // 학교/학년 불일치가 있으면 수정 모달 표시
          if (schoolGradeMismatches.length > 0) {
            setGradeWarningStudents(schoolGradeMismatches);
            setPendingStudents(studentsToUpload);
            setGradeWarnings(warnings);
            setMissingClasses(missingClassNames);
            // 반 생성 폼 초기화
            const initialForms = {};
            missingClassNames.forEach(name => {
              initialForms[name] = {
                name,
                subject: '',
                grade: '',
                maxStudents: 20,
                tuitionFee: 0,
                scheduleText: '',
                create: true
              };
            });
            setMissingClassForms(initialForms);
            setShowGradeWarningModal(true);
            return;
          }

          // 없는 반이 있으면 생성 모달 표시
          if (missingClassNames.length > 0) {
            setMissingClasses(missingClassNames);
            // 반 생성 폼 초기화
            const initialForms = {};
            missingClassNames.forEach(name => {
              initialForms[name] = {
                name,
                subject: '',
                grade: '',
                maxStudents: 20,
                tuitionFee: 0,
                scheduleText: '',  // 수업시간 텍스트 (예: 월 14:00-16:00, 수 14:00-16:00)
                create: true  // 생성 여부
              };
            });
            setMissingClassForms(initialForms);
            setGradeWarnings(warnings);
            setPendingStudents(studentsToUpload);
            setShowMissingClassModal(true);
          } else {
            // 없는 반 없으면 바로 업로드 (학년 경고만 표시)
            if (warnings.length > 0) {
              const warningMsg = warnings.map(w =>
                `${w.student}(${w.studentGrade}) → ${w.className}(대상: ${w.classGrade})`
              ).join('\n');

              if (!window.confirm(`학년 불일치 경고:\n${warningMsg}\n\n계속 진행하시겠습니까?`)) {
                return;
              }
            }
            await executeUpload(studentsToUpload);
          }
        } catch (err) {
          console.error('File processing error:', err);
          alert('파일 처리에 실패했습니다');
        }
      };
      reader.readAsArrayBuffer(uploadFile);
    } catch (error) {
      alert('업로드에 실패했습니다');
    }
  };

  // 학교/학년 불일치 수정 후 진행
  const handleProceedWithGradeWarning = async (skipFix = false) => {
    try {
      // 수정된 학년을 pendingStudents에 적용
      if (!skipFix) {
        const updatedStudents = [...pendingStudents];
        gradeWarningStudents.forEach(warning => {
          if (updatedStudents[warning.index]) {
            updatedStudents[warning.index].studentInfo.grade = warning.newGrade;
          }
        });
        setPendingStudents(updatedStudents);
      }

      setShowGradeWarningModal(false);

      // 없는 반이 있으면 없는 반 생성 모달로 이동
      if (missingClasses.length > 0) {
        setShowMissingClassModal(true);
      } else {
        // 바로 업로드 진행
        const latestStudents = skipFix ? pendingStudents : pendingStudents.map((student, index) => {
          const warning = gradeWarningStudents.find(w => w.index === index);
          if (warning) {
            return {
              ...student,
              studentInfo: { ...student.studentInfo, grade: warning.newGrade }
            };
          }
          return student;
        });
        await executeUpload(latestStudents);
      }
    } catch (error) {
      console.error('Error in handleProceedWithGradeWarning:', error);
      alert('처리 중 오류가 발생했습니다');
    }
  };

  // 없는 반 생성 후 학생 업로드 진행
  const handleProceedWithMissingClasses = async () => {
    try {
      // 생성 체크된 반들 생성
      const classesToCreate = Object.values(missingClassForms).filter(f => f.create);
      const createdClasses = [];

      for (const classForm of classesToCreate) {
        try {
          const schedule = parseScheduleText(classForm.scheduleText);
          const { data } = await api.post('/api/class-groups', {
            name: classForm.name,
            subject: classForm.subject,
            grade: classForm.grade,
            maxStudents: parseInt(classForm.maxStudents) || 20,
            tuitionFee: parseInt(classForm.tuitionFee) || 0,
            schedule
          });
          createdClasses.push(data.classGroup);
        } catch (err) {
          console.error(`Failed to create class ${classForm.name}:`, err);
          alert(`반 "${classForm.name}" 생성 실패: ${err.response?.data?.error || err.message}`);
        }
      }

      // 반 목록 새로고침
      const { data: classesData } = await api.get('/api/class-groups');
      setClasses(classesData.classGroups || []);

      // 학년 불일치 경고 표시
      if (gradeWarnings.length > 0) {
        const warningMsg = gradeWarnings.map(w =>
          `${w.student}(${w.studentGrade}) → ${w.className}(대상: ${w.classGrade})`
        ).join('\n');

        if (!window.confirm(`학년 불일치 경고:\n${warningMsg}\n\n계속 진행하시겠습니까?`)) {
          setShowMissingClassModal(false);
          return;
        }
      }

      // 학생 업로드 진행
      await executeUpload(pendingStudents, classesData.classGroups || []);
      setShowMissingClassModal(false);
    } catch (error) {
      console.error('Error in handleProceedWithMissingClasses:', error);
      alert('처리 중 오류가 발생했습니다');
    }
  };

  // 실제 학생 업로드 실행
  const executeUpload = async (studentsToUpload, latestClasses = null) => {
    const classesForLookup = latestClasses || classes;
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const student of studentsToUpload) {
      try {
        const className = student.className;
        delete student.className;

        const { data: userData } = await api.post('/api/users', student);

        // Auto-assign to class if specified
        if (className && userData.user) {
          const classGroup = classesForLookup.find(c => c.name === className);
          if (classGroup) {
            await api.post(`/api/class-groups/${classGroup._id}/students`, {
              studentIds: [userData.user.id]
            });
          }
        }
        successCount++;
      } catch (err) {
        console.error('Failed to create student:', err);
        const errorMsg = err.response?.data?.error || err.message;
        if (errorMsg.includes('duplicate') || errorMsg.includes('already exists') || errorMsg.includes('E11000')) {
          errors.push(`${student.name}: 이메일 중복 (${student.email})`);
        } else {
          errors.push(`${student.name}: ${errorMsg}`);
        }
        failCount++;
      }
    }

    setUploadResult({
      success: successCount,
      failed: failCount,
      errors
    });

    if (errors.length === 0) {
      alert(`업로드 완료: 성공 ${successCount}명`);
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadPreview([]);
    }

    loadData();
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['이름', '이메일', '전화번호', '학교', '학년', '학부모명', '학부모연락처', '반'],
      ['홍길동', 'hong@example.com', '010-1234-5678', '서울중학교', '중2', '홍부모', '010-8765-4321', '수학A반'],
      ['김영희', '', '01098765432', '서울중학교', '중2', '김부모', '01011112222', '수학A반'],
      ['이철수', '', '010-5555-6666', '한국고등학교', '고1', '', '', '영어B반']
    ]);

    // 컬럼 너비 설정
    ws['!cols'] = [
      { wch: 10 },  // 이름
      { wch: 25 },  // 이메일
      { wch: 15 },  // 전화번호
      { wch: 15 },  // 학교
      { wch: 8 },   // 학년
      { wch: 10 },  // 학부모명
      { wch: 15 },  // 학부모연락처
      { wch: 12 }   // 반
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '학생목록');
    XLSX.writeFile(wb, '학생_업로드_양식.xlsx');
  };

  return (
    <div className="students-page">
      <div className="page-header">
        <h2>학생 관리</h2>
        {isAdmin && (
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => { setUploadResult(null); setShowUploadModal(true); }}>
              <FiUpload /> 엑셀 업로드
            </button>
            <button className="btn btn-primary" onClick={() => openModal()}>
              <FiPlus /> 학생 추가
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="이름, 이메일, 전화번호로 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <button onClick={handleSearch}>
          <FiSearch />
        </button>
      </div>

      {/* Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>이름</th>
              <th>이메일</th>
              <th>전화번호</th>
              <th>학부모 연락처</th>
              <th>학교</th>
              <th>학년</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="loading">로딩 중...</td></tr>
            ) : filteredStudents.length === 0 ? (
              <tr><td colSpan="8" className="empty">{isAdmin ? '등록된 학생이 없습니다' : '맡은 학생이 없습니다'}</td></tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student._id} className={!student.isActive ? 'inactive-row' : ''}>
                  <td>{student.name}</td>
                  <td>{student.email}</td>
                  <td>{student.phone || '-'}</td>
                  <td>{student.studentInfo?.parentPhone || '-'}</td>
                  <td>{student.studentInfo?.school || '-'}</td>
                  <td>{student.studentInfo?.grade || '-'}</td>
                  <td>
                    <span className={`status ${student.isActive ? 'active' : 'inactive'}`}>
                      {student.isActive ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td>
                    <button className="icon-btn" onClick={() => openDetailModal(student)} title="상세">
                      <FiEye />
                    </button>
                    {isAdmin && (
                      <>
                        <button className="icon-btn" onClick={() => openModal(student)} title="수정">
                          <FiEdit2 />
                        </button>
                        <button
                          className={`icon-btn ${student.isActive ? 'warning' : 'success'}`}
                          onClick={() => handleToggleActive(student)}
                          title={student.isActive ? '비활성화' : '활성화'}
                        >
                          {student.isActive ? <FiUserX /> : <FiUserCheck />}
                        </button>
                        <button
                          className="icon-btn danger"
                          onClick={() => handleDelete(student)}
                          title="삭제"
                        >
                          <FiTrash2 />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingStudent ? '학생 수정' : '학생 추가'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>이름 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>이메일 *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingStudent}
                />
              </div>
              {!editingStudent && (
                <div className="form-group">
                  <label>비밀번호</label>
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="기본값: 123456"
                  />
                </div>
              )}
              <div className="form-group">
                <label>전화번호</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
                  placeholder="010-0000-0000"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>학교</label>
                  <input
                    type="text"
                    value={formData.studentInfo.school}
                    onChange={(e) => setFormData({
                      ...formData,
                      studentInfo: { ...formData.studentInfo, school: e.target.value }
                    })}
                    onBlur={(e) => {
                      const normalized = normalizeSchoolName(e.target.value);
                      if (normalized !== e.target.value) {
                        setFormData({
                          ...formData,
                          studentInfo: { ...formData.studentInfo, school: normalized }
                        });
                      }
                    }}
                    placeholder="예: 서울중학교"
                  />
                </div>
                <div className="form-group">
                  <label>학년</label>
                  <select
                    value={formData.studentInfo.grade}
                    onChange={(e) => setFormData({
                      ...formData,
                      studentInfo: { ...formData.studentInfo, grade: e.target.value }
                    })}
                  >
                    <option value="">선택</option>
                    <option value="초1">초1</option>
                    <option value="초2">초2</option>
                    <option value="초3">초3</option>
                    <option value="초4">초4</option>
                    <option value="초5">초5</option>
                    <option value="초6">초6</option>
                    <option value="중1">중1</option>
                    <option value="중2">중2</option>
                    <option value="중3">중3</option>
                    <option value="고1">고1</option>
                    <option value="고2">고2</option>
                    <option value="고3">고3</option>
                  </select>
                </div>
              </div>

              <hr style={{ margin: '16px 0', borderColor: '#eee' }} />
              <h4 style={{ marginBottom: '12px', color: '#666' }}>학부모 정보</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>학부모명</label>
                  <input
                    type="text"
                    value={formData.studentInfo.parentName}
                    onChange={(e) => setFormData({
                      ...formData,
                      studentInfo: { ...formData.studentInfo, parentName: e.target.value }
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>학부모 연락처 *</label>
                  <input
                    type="tel"
                    value={formData.studentInfo.parentPhone}
                    onChange={(e) => setFormData({
                      ...formData,
                      studentInfo: { ...formData.studentInfo, parentPhone: formatPhoneNumber(e.target.value) }
                    })}
                    placeholder="010-0000-0000"
                    required
                  />
                </div>
              </div>

              <hr style={{ margin: '16px 0', borderColor: '#eee' }} />
              <h4 style={{ marginBottom: '12px', color: '#666' }}>반 배정</h4>

              <div className="form-group">
                {!showNewClassInput ? (
                  <>
                    <label>반 선택</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select
                        value={formData.classId}
                        onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                        style={{ flex: 1 }}
                      >
                        <option value="">선택 안함</option>
                        {classes.filter(c => c.isActive).map(cls => (
                          <option key={cls._id} value={cls._id}>
                            {cls.name} {cls.grade && `(${cls.grade})`} - {cls.students?.length || 0}/{cls.maxStudents}명
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => setShowNewClassInput(true)}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        + 새 반
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '16px', backgroundColor: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <label style={{ margin: 0, fontWeight: 'bold' }}>새 반 생성</label>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => {
                          setShowNewClassInput(false);
                          setNewClassData({ name: '', subject: '', grade: '', maxStudents: 20, tuitionFee: 0, scheduleText: '' });
                        }}
                      >
                        취소
                      </button>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>반 이름 *</label>
                        <input
                          type="text"
                          placeholder="예: 중등 수학반"
                          value={newClassData.name}
                          onChange={(e) => setNewClassData({ ...newClassData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>과목</label>
                        <input
                          type="text"
                          placeholder="예: 수학"
                          value={newClassData.subject}
                          onChange={(e) => setNewClassData({ ...newClassData, subject: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>대상 학년</label>
                        <input
                          type="text"
                          placeholder="예: 중1-3"
                          value={newClassData.grade}
                          onChange={(e) => setNewClassData({ ...newClassData, grade: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>최대 인원</label>
                        <input
                          type="number"
                          value={newClassData.maxStudents}
                          onChange={(e) => setNewClassData({ ...newClassData, maxStudents: e.target.value })}
                          min="1"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>월 수강료</label>
                        <input
                          type="number"
                          placeholder="예: 300000"
                          value={newClassData.tuitionFee}
                          onChange={(e) => setNewClassData({ ...newClassData, tuitionFee: e.target.value })}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>수업 시간</label>
                        <input
                          type="text"
                          placeholder="예: 월 14:00-16:00, 수 14:00-16:00"
                          value={newClassData.scheduleText}
                          onChange={(e) => setNewClassData({ ...newClassData, scheduleText: e.target.value })}
                        />
                      </div>
                    </div>

                    <small style={{ color: '#888', display: 'block' }}>
                      새 반을 생성하고 학생을 배정합니다
                    </small>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingStudent ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>엑셀 업로드</h3>
              <button className="close-btn" onClick={() => setShowUploadModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="upload-content">
              <div className="upload-info">
                <p>엑셀 파일(.xlsx)로 학생을 일괄 등록할 수 있습니다.</p>
                <button className="btn btn-outline btn-sm" onClick={downloadTemplate}>
                  <FiDownload /> 양식 다운로드
                </button>
              </div>

              <div className="upload-area">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn btn-secondary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiUpload /> 파일 선택
                </button>
                {uploadFile && (
                  <span className="file-name">{uploadFile.name}</span>
                )}
              </div>

              {uploadPreview.length > 0 && (
                <div className="upload-preview">
                  <h4>미리보기 (최대 5건)</h4>
                  <table>
                    <thead>
                      <tr>
                        {Object.keys(uploadPreview[0]).map(key => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadPreview.map((row, i) => (
                        <tr key={i}>
                          {Object.values(row).map((val, j) => (
                            <td key={j}>{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 업로드 결과 표시 */}
              {uploadResult && (
                <div className="upload-result">
                  <div className="upload-summary">
                    <span className="success-count">성공: {uploadResult.success}명</span>
                    {uploadResult.failed > 0 && (
                      <span className="fail-count">실패: {uploadResult.failed}명</span>
                    )}
                  </div>
                  {uploadResult.errors.length > 0 && (
                    <div className="upload-errors">
                      <h5>실패 사유:</h5>
                      <ul>
                        {uploadResult.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>
                  취소
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={!uploadFile}
                >
                  업로드
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grade Warning Modal - 학교/학년 불일치 수정 */}
      {showGradeWarningModal && (
        <div className="modal-overlay" onClick={() => setShowGradeWarningModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>학교/학년 불일치 확인</h3>
              <button className="close-btn" onClick={() => setShowGradeWarningModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#666' }}>
                학교 종류와 학년이 맞지 않는 학생이 있습니다. 학년을 수정하거나 그대로 진행할 수 있습니다.
              </p>
              <p style={{ marginBottom: '16px', fontSize: '12px', color: '#888' }}>
                예: 중학교 → 중1~중3, 고등학교 → 고1~고3, 초등학교 → 초1~초6
              </p>

              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>학생명</th>
                      <th>학교</th>
                      <th>현재 학년</th>
                      <th>수정 학년</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradeWarningStudents.map((warning, idx) => (
                      <tr key={idx}>
                        <td>{warning.name}</td>
                        <td>{warning.school}</td>
                        <td style={{ color: '#ef4444' }}>{warning.grade}</td>
                        <td>
                          <select
                            value={warning.newGrade}
                            onChange={(e) => {
                              const updated = [...gradeWarningStudents];
                              updated[idx].newGrade = e.target.value;
                              setGradeWarningStudents(updated);
                            }}
                            style={{ width: '100%', padding: '4px' }}
                          >
                            <optgroup label="초등학교">
                              <option value="초1">초1</option>
                              <option value="초2">초2</option>
                              <option value="초3">초3</option>
                              <option value="초4">초4</option>
                              <option value="초5">초5</option>
                              <option value="초6">초6</option>
                            </optgroup>
                            <optgroup label="중학교">
                              <option value="중1">중1</option>
                              <option value="중2">중2</option>
                              <option value="중3">중3</option>
                            </optgroup>
                            <optgroup label="고등학교">
                              <option value="고1">고1</option>
                              <option value="고2">고2</option>
                              <option value="고3">고3</option>
                            </optgroup>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowGradeWarningModal(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => handleProceedWithGradeWarning(true)}
              >
                수정 없이 진행
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleProceedWithGradeWarning(false)}
              >
                수정 후 진행
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Missing Class Creation Modal */}
      {showMissingClassModal && (
        <div className="modal-overlay" onClick={() => setShowMissingClassModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>존재하지 않는 반 발견</h3>
              <button className="close-btn" onClick={() => setShowMissingClassModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#666' }}>
                엑셀 파일에 존재하지 않는 반이 있습니다. 아래에서 반을 생성하거나 건너뛸 수 있습니다.
              </p>

              {missingClasses.map((className) => (
                <div key={className} className="missing-class-form" style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '12px',
                  backgroundColor: missingClassForms[className]?.create ? '#f8f9fa' : '#fff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ margin: 0 }}>{className}</h4>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={missingClassForms[className]?.create || false}
                        onChange={(e) => setMissingClassForms({
                          ...missingClassForms,
                          [className]: { ...missingClassForms[className], create: e.target.checked }
                        })}
                      />
                      반 생성
                    </label>
                  </div>

                  {missingClassForms[className]?.create && (
                    <>
                      <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                        <div className="form-group">
                          <label>과목</label>
                          <input
                            type="text"
                            placeholder="예: 수학"
                            value={missingClassForms[className]?.subject || ''}
                            onChange={(e) => setMissingClassForms({
                              ...missingClassForms,
                              [className]: { ...missingClassForms[className], subject: e.target.value }
                            })}
                          />
                        </div>
                        <div className="form-group">
                          <label>대상 학년</label>
                          <input
                            type="text"
                            placeholder="예: 중2-3"
                            value={missingClassForms[className]?.grade || ''}
                            onChange={(e) => setMissingClassForms({
                              ...missingClassForms,
                              [className]: { ...missingClassForms[className], grade: e.target.value }
                            })}
                          />
                        </div>
                        <div className="form-group">
                          <label>최대 인원</label>
                          <input
                            type="number"
                            min="1"
                            value={missingClassForms[className]?.maxStudents || 20}
                            onChange={(e) => setMissingClassForms({
                              ...missingClassForms,
                              [className]: { ...missingClassForms[className], maxStudents: e.target.value }
                            })}
                          />
                        </div>
                        <div className="form-group">
                          <label>월 수강료</label>
                          <input
                            type="number"
                            min="0"
                            value={missingClassForms[className]?.tuitionFee || 0}
                            onChange={(e) => setMissingClassForms({
                              ...missingClassForms,
                              [className]: { ...missingClassForms[className], tuitionFee: e.target.value }
                            })}
                          />
                        </div>
                      </div>
                      <div className="form-group" style={{ marginTop: '8px' }}>
                        <label>수업시간</label>
                        <input
                          type="text"
                          placeholder="예: 월 14:00-16:00, 수 14:00-16:00"
                          value={missingClassForms[className]?.scheduleText || ''}
                          onChange={(e) => setMissingClassForms({
                            ...missingClassForms,
                            [className]: { ...missingClassForms[className], scheduleText: e.target.value }
                          })}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}

              <p style={{ marginTop: '16px', fontSize: '13px', color: '#888' }}>
                * "반 생성" 체크를 해제하면 해당 반에 학생 배정은 건너뜁니다.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowMissingClassModal(false)}>
                취소
              </button>
              <button className="btn btn-primary" onClick={handleProceedWithMissingClasses}>
                확인 및 진행
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Detail Modal */}
      {showDetailModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FiUser /> {selectedStudent.name} 학생 상세
              </h3>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <FiX />
              </button>
            </div>

            {/* Student Info Summary */}
            <div className="student-summary">
              <div className="summary-item">
                <span className="label">학교</span>
                <span className="value">{selectedStudent.studentInfo?.school || '-'}</span>
              </div>
              <div className="summary-item">
                <span className="label">학년</span>
                <span className="value">{selectedStudent.studentInfo?.grade || '-'}</span>
              </div>
              <div className="summary-item">
                <span className="label">연락처</span>
                <span className="value">{selectedStudent.phone || '-'}</span>
              </div>
              <div className="summary-item">
                <span className="label">학부모</span>
                <span className="value">
                  {selectedStudent.studentInfo?.parentName || '-'}
                  {selectedStudent.studentInfo?.parentPhone && ` (${selectedStudent.studentInfo.parentPhone})`}
                </span>
              </div>
            </div>

            {/* Tabs */}
            <div className="detail-tabs">
              <button
                className={`tab-btn ${detailTab === 'attendance' ? 'active' : ''}`}
                onClick={() => setDetailTab('attendance')}
              >
                <FiCalendar /> 출결 ({studentDetail.attendance.length})
              </button>
              <button
                className={`tab-btn ${detailTab === 'lessons' ? 'active' : ''}`}
                onClick={() => setDetailTab('lessons')}
              >
                <FiFileText /> 수업일지 ({studentDetail.lessons.length})
              </button>
              <button
                className={`tab-btn ${detailTab === 'tests' ? 'active' : ''}`}
                onClick={() => setDetailTab('tests')}
              >
                <FiClipboard /> 시험 ({studentDetail.tests.length})
              </button>
              {isAdmin && (
                <button
                  className={`tab-btn ${detailTab === 'tuition' ? 'active' : ''}`}
                  onClick={() => setDetailTab('tuition')}
                >
                  <FiDollarSign /> 수납 ({studentDetail.tuitions.length})
                </button>
              )}
              <button
                className={`tab-btn ${detailTab === 'counsel' ? 'active' : ''}`}
                onClick={() => setDetailTab('counsel')}
              >
                <FiUser /> 상담 ({studentDetail.counsels.length})
              </button>
            </div>

            {/* Tab Content */}
            <div className="detail-content">
              {detailLoading ? (
                <div className="loading">로딩 중...</div>
              ) : (
                <>
                  {/* Attendance Tab */}
                  {detailTab === 'attendance' && (
                    <div className="detail-list">
                      {studentDetail.attendance.length === 0 ? (
                        <div className="empty">출결 기록이 없습니다</div>
                      ) : (
                        studentDetail.attendance.map((rec) => {
                          const status = getAttendanceStatus(rec.status);
                          return (
                            <div key={rec._id} className="detail-item attendance-item">
                              <span className="date">{format(new Date(rec.date), 'yyyy-MM-dd')}</span>
                              <span className={`badge badge-${status.color}`}>{status.label}</span>
                              <span className="time-info">
                                등원: {rec.checkIn?.time ? format(new Date(rec.checkIn.time), 'HH:mm') : '-'}
                                {' / '}
                                하원: {rec.checkOut?.time ? format(new Date(rec.checkOut.time), 'HH:mm') : '-'}
                              </span>
                              {rec.note && <span className="note">{rec.note}</span>}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  {/* Lessons Tab */}
                  {detailTab === 'lessons' && (
                    <div className="detail-list">
                      {studentDetail.lessons.length === 0 ? (
                        <div className="empty">수업일지가 없습니다</div>
                      ) : (
                        studentDetail.lessons.map((lesson) => (
                          <div key={lesson._id} className="detail-item lesson-item">
                            <div className="lesson-header">
                              <span className="date">{format(new Date(lesson.date), 'yyyy-MM-dd')}</span>
                              <span className="class-name">{lesson.classGroupId?.name || '-'}</span>
                              <span className="subject">{lesson.classGroupId?.subject || '-'}</span>
                            </div>
                            <div className="lesson-title">{lesson.title || '-'}</div>
                            {lesson.content && <div className="lesson-content">{lesson.content}</div>}
                            {lesson.homework && <div className="homework">과제: {lesson.homework}</div>}
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Tests Tab */}
                  {detailTab === 'tests' && (
                    <div className="detail-list">
                      {studentDetail.tests.length === 0 ? (
                        <div className="empty">시험 기록이 없습니다</div>
                      ) : (
                        studentDetail.tests.map((result) => (
                          <div key={result._id} className="detail-item test-item">
                            <span className="test-name">{result.testTemplateId?.name || '-'}</span>
                            <span className="subject">{result.testTemplateId?.subject || '-'}</span>
                            <span className="score">
                              {result.score !== null && result.score !== undefined
                                ? `${result.score}점`
                                : '-'}
                            </span>
                            {result.feedback && <span className="note">{result.feedback}</span>}
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Tuition Tab - Admin Only */}
                  {isAdmin && detailTab === 'tuition' && (
                    <div className="detail-list">
                      {studentDetail.tuitions.length === 0 ? (
                        <div className="empty">수납 기록이 없습니다</div>
                      ) : (
                        studentDetail.tuitions.map((tuition) => {
                          const status = getTuitionStatus(tuition.status);
                          return (
                            <div key={tuition._id} className="detail-item tuition-item">
                              <span className="month">{tuition.billingMonth}</span>
                              <span className="amount">{tuition.finalAmount?.toLocaleString()}원</span>
                              <span className="paid">{tuition.paidAmount?.toLocaleString()}원 납부</span>
                              <span className={`badge badge-${status.color}`}>{status.label}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  {/* Counsel Tab */}
                  {detailTab === 'counsel' && (
                    <div className="detail-list">
                      {studentDetail.counsels.length === 0 ? (
                        <div className="empty">상담 기록이 없습니다</div>
                      ) : (
                        studentDetail.counsels.map((counsel) => (
                          <div key={counsel._id} className="detail-item counsel-item">
                            <div className="counsel-header">
                              <span className="date">{format(new Date(counsel.date), 'yyyy-MM-dd')}</span>
                              <span className={`badge ${counsel.type === 'parent' ? 'badge-primary' : 'badge-info'}`}>
                                {counsel.type === 'parent' ? '학부모' : '학생'}
                              </span>
                              <span className="method">{counsel.method === 'phone' ? '전화' : counsel.method === 'visit' ? '방문' : counsel.method}</span>
                            </div>
                            <div className="counsel-content">{counsel.content}</div>
                            <div className="counsel-footer">
                              <span className="author">- {counsel.createdBy?.name || '-'}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
